// @ts-check
// Cabeçalho da coluna do kanban (doc 06, seção 6.2): etiqueta, contagem, agregações escolhidas na tela, menu da
// coluna (mover, ocultar, ocultar colunas vazias, modo compacto, editar as etapas) e alça de largura.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { getFieldIcon, isFieldDisplayable } from '../../lib/field-metadata.js';
import { Tag } from '../../ui/chip.js';
import { LightIconButton } from '../../ui/button.js';
import { Toggle } from '../../ui/feedback.js';
import { Dropdown, DropdownMenuHeader, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { persistViewPatch } from '../views/view-options.js';
import { AggregateOperationMenu, getAggregateShortLabel } from './aggregate-menu.js';
import {
  MAXIMUM_KANBAN_AGGREGATES,
  addKanbanAggregate,
  getAggregateSubFieldName,
  getAvailableAggregateOperations,
  moveGroup,
  removeKanbanAggregate,
  setGroupVisibility,
} from './view-layout.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 * @typedef {{ key: string, value: string | null, label: string, color: string }} BoardColumn
 * @typedef {{ operation: string, field: FieldMetadata, parameter: string }} BoardAggregate
 */

/** Agregações gravadas na view, na forma da API (a lista nova ou a operação única antiga). @param {View} view */
export function getSavedKanbanAggregates(view) {
  if (view.kanbanAggregates?.length) return view.kanbanAggregates.map((item) => ({ operation: item.operation, fieldMetadataId: item.fieldMetadataId ?? null, subFieldName: item.subFieldName ?? null }));
  if (view.kanbanAggregateOperation) return [{ operation: view.kanbanAggregateOperation, fieldMetadataId: view.kanbanAggregateOperationFieldMetadataId ?? null, subFieldName: null }];
  return [];
}

/**
 * Onde editar as opções do campo das colunas: Funil e etapas para a etapa do card, a tela do campo nos outros casos.
 * @param {ObjectMetadata} objectMetadata @param {FieldMetadata} groupField
 */
const getGroupFieldSettingsPath = (objectMetadata, groupField) => (objectMetadata.nameSingular === 'enrollmentOpportunity' && groupField.name === 'stage'
  ? '/settings/funnel'
  : `/settings/objects/${objectMetadata.namePlural}/${groupField.name}`);

/**
 * Menu das agregações do cabeçalho: lista as escolhidas, tira e adiciona (campo e depois operação). Vale para todas
 * as colunas, como no Twenty.
 * @param {{ objectMetadata: ObjectMetadata, view: View, aggregates: BoardAggregate[], onClose: () => void }} props
 */
function BoardAggregatesMenu({ objectMetadata, view, aggregates, onClose }) {
  const [mode, setMode] = useState(/** @type {'list' | 'field' | 'operation'} */ ('list'));
  const [chosenField, setChosenField] = useState(/** @type {FieldMetadata | null} */ (null));
  const saved = getSavedKanbanAggregates(view);
  // contar todos já é a contagem da coluna; só entram campos com outra operação
  const fields = objectMetadata.fields.filter((field) => isFieldDisplayable(field) && getAvailableAggregateOperations(field).some((operation) => operation !== 'COUNT'));

  if (mode === 'field') {
    return html`
      <${DropdownMenuHeader} title=${t('boardTable.board.chooseField')} onBack=${() => setMode('list')} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${fields.map((field) => html`<${MenuItem} key=${field.id} icon=${getFieldIcon(field)} text=${translateMetadataLabel(field.label)} hasSubMenu=${true}
          onClick=${() => {
            setChosenField(field);
            setMode('operation');
          }} />`)}
      <//>`;
  }
  if (mode === 'operation' && chosenField) {
    return html`
      <${DropdownMenuHeader} title=${translateMetadataLabel(chosenField.label)} onBack=${() => setMode('field')} />
      <${AggregateOperationMenu} field=${chosenField} allowNone=${false} excluded=${['COUNT']} onSelect=${(operation) => {
        if (!operation) return;
        persistViewPatch(view, { kanbanAggregates: addKanbanAggregate(saved, { operation, fieldMetadataId: chosenField.id, subFieldName: getAggregateSubFieldName(chosenField) }) });
        onClose();
      }} />`;
  }
  return html`
    <${DropdownMenuHeader} title=${t('boardTable.board.aggregatesTitle')} />
    <${DropdownMenuItems}>
      <${MenuItem} icon="IconCheck" text=${t('boardTable.aggregates.COUNT')} contextualText=${t('boardTable.board.alwaysShown')} disabled=${true} />
      ${aggregates.map((aggregate) => {
        const savedIndex = saved.findIndex((item) => item.operation === aggregate.operation && item.fieldMetadataId === aggregate.field.id);
        return html`<${MenuItem}
          key=${aggregate.parameter}
          icon=${getFieldIcon(aggregate.field)}
          text=${`${getAggregateShortLabel(aggregate.operation)}: ${translateMetadataLabel(aggregate.field.label)}`}
          rightComponent=${html`<${LightIconButton} icon="IconX" accent="tertiary" ariaLabel=${t('boardTable.board.removeAggregate')} onClick=${(/** @type {Event} */ event) => {
            event.stopPropagation();
            if (savedIndex >= 0) persistViewPatch(view, { kanbanAggregates: removeKanbanAggregate(saved, savedIndex) });
          }} />`}
        />`;
      })}
      <${DropdownMenuSeparator} />
      <${MenuItem} icon="IconPlus" text=${t('boardTable.board.addAggregate')} disabled=${saved.length >= MAXIMUM_KANBAN_AGGREGATES} hasSubMenu=${true}
        onClick=${() => setMode('field')} />
    <//>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View, groupField: FieldMetadata, column: BoardColumn, isFirst: boolean, isLast: boolean,
 *   countLabel: string, aggregates: BoardAggregate[], aggregateLabels: { key: string, text: string, title: string }[],
 *   onAddNew: () => void, onResizeStart: (event: PointerEvent) => void, canCreate: boolean }} props
 */
export function BoardColumnHeader({ objectMetadata, view, groupField, column, isFirst, isLast, countLabel, aggregates, aggregateLabels, onAddNew, onResizeStart, canCreate }) {
  const menuRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const aggregateRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAggregateOpen, setIsAggregateOpen] = useState(false);
  const canEditStages = hasCurrentPermissionFlag('DATA_MODEL');
  const runAndClose = (/** @type {() => void} */ action) => () => {
    setIsMenuOpen(false);
    action();
  };

  return html`<div class="board-column-header" data-menu-open=${isMenuOpen || isAggregateOpen ? 'true' : undefined}>
    <div class="board-column-title-row">
      <span class="board-column-tag"><${Tag} color=${column.color} text=${column.label} variant=${column.value === null ? 'outline' : 'solid'} weight="medium" /></span>
      <button ref=${aggregateRef} type="button" class="board-column-count" data-testid="board-column-count" title=${t('boardTable.board.aggregatesHint')}
        onClick=${() => setIsAggregateOpen(!isAggregateOpen)}>${countLabel}</button>
      <span class="board-column-actions">
        <span ref=${menuRef}>
          <${LightIconButton} icon="IconDotsVertical" accent="tertiary" ariaLabel=${t('boardTable.board.columnMenu')} data-testid="board-column-menu" onClick=${() => setIsMenuOpen(!isMenuOpen)} />
        </span>
        ${canCreate ? html`<${LightIconButton} icon="IconPlus" accent="tertiary" ariaLabel=${t('board.addNew')} onClick=${onAddNew} />` : null}
      </span>
    </div>
    ${aggregateLabels.length > 0
      ? html`<button type="button" class="board-column-aggregates" title=${t('boardTable.board.aggregatesHint')} onClick=${() => setIsAggregateOpen(!isAggregateOpen)}>
          ${aggregateLabels.map((label) => html`<span key=${label.key} class="board-column-aggregate" title=${label.title}>${label.text}</span>`)}
        </button>`
      : null}
    <span class="board-column-resize-handle" role="separator" aria-orientation="vertical" aria-label=${t('boardTable.board.resizeColumns')}
      onPointerDown=${onResizeStart}></span>
    <${Dropdown} anchorRef=${aggregateRef} isOpen=${isAggregateOpen} onClose=${() => setIsAggregateOpen(false)} width=${260}>
      <${BoardAggregatesMenu} objectMetadata=${objectMetadata} view=${view} aggregates=${aggregates} onClose=${() => setIsAggregateOpen(false)} />
    <//>
    <${Dropdown} anchorRef=${menuRef} isOpen=${isMenuOpen} onClose=${() => setIsMenuOpen(false)} width=${240} placement="bottom-end">
      <${DropdownMenuItems}>
        ${canEditStages
          ? html`<${MenuItem} icon="IconSettings" text=${t('boardTable.board.editStages', { field: translateMetadataLabel(groupField.label).toLocaleLowerCase('pt-BR') })}
              onClick=${runAndClose(() => navigate(getGroupFieldSettingsPath(objectMetadata, groupField)))} />`
          : null}
        <${MenuItem} icon="IconArrowLeft" text=${t('table.columnMenu.moveLeft')} disabled=${isFirst}
          onClick=${runAndClose(() => persistViewPatch(view, { viewGroups: moveGroup(view.viewGroups, groupField, column.key, -1) }))} />
        <${MenuItem} icon="IconArrowRight" text=${t('table.columnMenu.moveRight')} disabled=${isLast}
          onClick=${runAndClose(() => persistViewPatch(view, { viewGroups: moveGroup(view.viewGroups, groupField, column.key, 1) }))} />
        <${MenuItem} icon="IconEyeOff" text=${t('table.columnMenu.hide')}
          onClick=${runAndClose(() => persistViewPatch(view, { viewGroups: setGroupVisibility(view.viewGroups, groupField, column.key, false) }))} />
        <${DropdownMenuSeparator} />
        <${MenuItem} icon="IconCircleOff" text=${t('boardTable.options.hideEmptyColumns')}
          onClick=${() => persistViewPatch(view, { shouldHideEmptyGroups: !view.shouldHideEmptyGroups })}
          rightComponent=${html`<${Toggle} size="small" value=${Boolean(view.shouldHideEmptyGroups)} ariaLabel=${t('boardTable.options.hideEmptyColumns')}
            onChange=${(/** @type {boolean} */ value) => persistViewPatch(view, { shouldHideEmptyGroups: value })} />`} />
        <${MenuItem} icon="IconBaselineDensitySmall" text=${t('boardTable.options.compact')}
          onClick=${() => persistViewPatch(view, { isCompact: !view.isCompact })}
          rightComponent=${html`<${Toggle} size="small" value=${Boolean(view.isCompact)} ariaLabel=${t('boardTable.options.compact')}
            onChange=${(/** @type {boolean} */ value) => persistViewPatch(view, { isCompact: value })} />`} />
      <//>
    <//>
  </div>`;
}
